'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useStickyOnScrollUp} from '../../hooks';
import {Button} from '../primitives';
import {useMobileRegisterInterestSuppressed} from './mobile-register-interest-suppression';
import {usePageRegisterInterestContext} from './page-register-interest-context';
import {openRegisterInterest} from './register-interest-controller';

// Mobile-only bar that slides in on scroll-up and is hidden from `md` up.
const StickyBar = styled(Box, {shouldForwardProp: propName => propName !== '$visible'})<{$visible: boolean}>(({theme, $visible}) => ({
  position: 'fixed',
  insetInline: 0,
  bottom: 0,
  zIndex: theme.zIndex.appBar,
  padding: theme.spacing(2),
  paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom))`,
  background: theme.palette.colors.surface.neutral.inverseSubtle,
  backdropFilter: 'blur(40.5px)',
  WebkitBackdropFilter: 'blur(40.5px)',
  transform: $visible ? 'translateY(0)' : 'translateY(140%)',
  opacity: $visible ? 1 : 0,
  pointerEvents: $visible ? 'auto' : 'none',
  transition: 'transform 240ms ease, opacity 240ms ease',
  borderTopLeftRadius: theme.radii['2xl'],
  borderTopRightRadius: theme.radii['2xl'],
  [theme.breakpoints.up('md')]: {display: 'none'},
}));

// The bar is fixed, so without an in-flow twin of its height the end of the page
// (footer social links, copyright) can never scroll out from under it.
const BarSpacer = styled(Box)(({theme}) => ({
  background: theme.palette.colors.surface.footer.default,
  [theme.breakpoints.up('md')]: {display: 'none'},
}));

function useElementHeight<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.borderBoxSize[0]?.blockSize ?? element.offsetHeight);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);
  return {ref, height};
}

interface MobileStickyRegisterInterestProps {
  onClick?: () => void;
}

export function MobileStickyRegisterInterest({onClick}: MobileStickyRegisterInterestProps) {
  const {t} = useTranslation();
  const {visible} = useStickyOnScrollUp();
  const suppressed = useMobileRegisterInterestSuppressed();
  // No target of its own — the bar is layout-mounted on every page — so it opens
  // with whatever lead context the current page published (none off a detail page).
  const pageContext = usePageRegisterInterestContext();
  const {ref, height} = useElementHeight<HTMLDivElement>();
  if (suppressed) {
    return null;
  }
  return (
    <>
      <BarSpacer aria-hidden sx={{height}} />
      <StickyBar ref={ref} $visible={visible}>
        <Button
          fullWidth
          onClick={() => {
            onClick?.();
            openRegisterInterest(pageContext);
          }}
        >
          {t('register_interest')}
        </Button>
      </StickyBar>
    </>
  );
}

MobileStickyRegisterInterest.displayName = 'MobileStickyRegisterInterest';
